import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ASSETS } from "@/lib/assets";
import { useDetectedCity } from "@/lib/hooks";

const EASE = [0.2, 0.7, 0.2, 1];

const SCREENS = [
  ASSETS.screenshots.onboarding,
  ASSETS.screenshots.swipe,
  ASSETS.screenshots.chat,
  ASSETS.screenshots.events,
];

export const APP_STORE_URL =
  "https://apps.apple.com/fr/app/mamamatch/id6794099163";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.mamamatch.mobile&pcampaignid=web_share";

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="mask-line">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function AppleIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.564 12.65c-.023-2.505 2.045-3.712 2.14-3.77-1.17-1.712-2.988-1.945-3.632-1.972-1.547-.156-3.02.912-3.803.912-.783 0-1.994-.888-3.277-.865-1.687.025-3.242.98-4.11 2.49-1.752 3.032-.449 7.523 1.263 9.988.836 1.208 1.833 2.564 3.144 2.516 1.263-.051 1.74-.816 3.267-.816 1.527 0 1.956.816 3.293.79 1.36-.024 2.222-1.231 3.055-2.44.962-1.4 1.359-2.756 1.383-2.826-.03-.014-2.652-1.02-2.723-4.007ZM15.09 5.34c.693-.856 1.166-2.04.036-3.24-1.005 1.086-1.207 2.29-.984 3.24.83.038 1.71-.406 2.4-1.24Z"/>
    </svg>
  );
}

function PlayIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#00D2FF" d="M3.61 1.81a1.98 1.98 0 0 0-.42 1.32v17.74c0 .49.15.94.42 1.32L14 12 3.61 1.81Z"/>
      <path fill="#FFC908" d="m17.11 15.51-2.76-2.76L11.32 12l3.03-2.75 2.76-2.76 3.78 2.15c1.15.65 1.15 2.3 0 2.96l-3.78 1.91Z"/>
      <path fill="#F94336" d="M14.35 12 3.61 22.19c.4.56 1.05.79 1.7.43l12.8-7.26L14.35 12Z"/>
      <path fill="#00E88F" d="M14.35 12 5.31 1.38c-.65-.36-1.3-.13-1.7.43L14.35 12Z"/>
    </svg>
  );
}

function StoreButton({ href, icon: Icon, label, sub, primary = false, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`group inline-flex items-center gap-3 rounded-2xl px-5 py-3.5 md:px-6 md:py-4 border transition-all ${
        primary
          ? "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#1c1c1c]"
          : "bg-white text-[#0A0A0A] border-[rgba(10,10,10,0.14)] hover:border-[rgba(10,10,10,0.35)]"
      }`}
    >
      <Icon className="w-7 h-7 md:w-8 md:h-8 shrink-0" />
      <div className="flex flex-col items-start leading-tight">
        <span
          className={`text-[11px] md:text-[12px] font-bold uppercase tracking-[0.14em] ${
            primary ? "text-white" : "text-[#0A0A0A]"
          }`}
        >
          {sub}
        </span>
        <span className="text-[16px] md:text-[18px] font-semibold">
          {label}
        </span>
      </div>
    </a>
  );
}

export default function Hero() {
  const [i, setI] = useState(0);
  const city = useDetectedCity();
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;
    const t = setInterval(() => setI((v) => (v + 1) % SCREENS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-24 md:pt-32 pb-10 md:pb-16 bg-white"
    >
      <div className="mx-auto max-w-[900px] px-5 md:px-8 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="inline-flex items-center gap-2 rounded-full border border-[rgba(10,10,10,0.08)] bg-white px-3.5 py-1.5 mb-5 md:mb-6"
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-[#D97757]"
            aria-hidden="true"
          />
          <span
            data-testid="hero-eyebrow"
            className="text-[12px] font-medium text-[#0A0A0A]/75"
          >
            {city ? (
              <>Rejoins les mamans de <span className="text-[#0A0A0A]">{city}</span></>
            ) : (
              "Disponible sur iOS & Android"
            )}
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display text-[38px] sm:text-[54px] md:text-[68px] lg:text-[80px] text-[#0A0A0A]"
        >
          <MaskLine delay={0.1}>MamaMatch est</MaskLine>
          <MaskLine delay={0.24}>
            enfin <span className="text-[#D97757]">là.</span>
          </MaskLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
          className="mt-5 md:mt-6 max-w-[560px] text-[15px] md:text-[17px] leading-[1.55] text-[#6B6660] font-normal"
        >
          Rencontre des mamans près de chez toi qui vivent la même étape que
          toi. Télécharge l'app maintenant, sur iOS et Android.
        </motion.p>

        <motion.div
          id="download"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.65 }}
          className="mt-6 md:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3"
        >
          <StoreButton
            href={APP_STORE_URL}
            icon={AppleIcon}
            label="App Store"
            sub="Télécharger sur"
            primary
            testId="hero-cta-appstore"
          />
          <StoreButton
            href={PLAY_STORE_URL}
            icon={PlayIcon}
            label="Google Play"
            sub="Télécharger sur"
            testId="hero-cta-playstore"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-5 md:mt-6 flex items-center justify-center gap-3 text-[13px] text-[#6B6660]"
        >
          <div className="flex -space-x-2">
            {ASSETS.testimonials.slice(0, 3).map((src, k) => (
              <div
                key={k}
                className="w-7 h-7 rounded-full ring-2 ring-white overflow-hidden bg-[#F7F5F2]"
              >
                <img
                  src={src}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          <span data-testid="hero-social-proof">
            Une communauté de mamans qui grandit chaque jour
          </span>
        </motion.div>

        {/* PHONE below buttons */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
          className="relative w-[190px] sm:w-[220px] md:w-[240px] mt-12 md:mt-16"
        >
          <div className="mm-iphone">
            <div className="mm-iphone-screen">
              <AnimatePresence mode="wait">
                <motion.img
                  key={i}
                  src={SCREENS[i]}
                  alt="Aperçu MamaMatch"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
